import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight } from "lucide-react";
import TopHeader from "../components/TopHeader";
import Footer from "../components/Footer";
import SeoHead from "../components/SeoHead";
import { BLOG_POSTS } from "../data/blogData";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
}

export default function BlogPage() {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SeoHead
        title="Blog — API to MCP Converter"
        description="Technical guides, tutorials, and insights on MCP servers, Claude integrations, and connecting AI agents to real APIs."
        canonical="/blog"
      />
      <TopHeader />

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 pt-16 pb-24">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <h1 className="text-4xl font-bold tracking-tight mb-3">Blog</h1>
          <p className="text-muted-foreground text-lg mb-12">
            Practical guides on MCP, Claude integrations, and building with AI agents.
          </p>
        </motion.div>

        <div className="space-y-8">
          {BLOG_POSTS.map((post, i) => (
            <motion.article
              key={post.slug}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.4 }}
            >
              <Link to={`/blog/${post.slug}`} className="group block">
                {/* Cover strip */}
                <div className={`h-2 rounded-t-2xl bg-gradient-to-r ${post.coverGradient}`} />

                <div className="rounded-b-2xl border border-t-0 border-border bg-card p-7 group-hover:border-border/80 transition-colors">
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.map(tag => (
                      <span key={tag} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <h2 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors leading-snug">
                    {post.title}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                    {post.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{formatDate(post.publishedAt)}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {post.readingMins} min read
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-primary group-hover:gap-2 transition-all">
                      Read <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
